import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { t } = useLang();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("signin");
  const [mode, setMode] = useState("form"); // "form" | "forgot" | "forgot-sent"

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-heading text-5xl font-medium text-stone-900">
            Julia<span className="italic text-[#9F8E7E]"> AI</span>
          </h1>
          <p className="text-sm text-stone-500 mt-3">{t("loginSubtitle")}</p>
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl p-8">
          {mode === "forgot" && (
            <ForgotForm onBack={() => setMode("form")} onSent={() => setMode("forgot-sent")} t={t} />
          )}

          {mode === "forgot-sent" && (
            <div className="text-center py-4" data-testid="forgot-sent-message">
              <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-4">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className="text-stone-700"><path d="M4 6h16v12H4z"/><path d="m4 7 8 6 8-6"/></svg>
              </div>
              <h2 className="font-heading text-xl font-medium text-stone-900 mb-2">{t("checkYourInbox")}</h2>
              <p className="text-sm text-stone-500 mb-6">{t("resetSentBody")}</p>
              <button
                data-testid="back-to-signin"
                onClick={() => { setMode("form"); setTab("signin"); }}
                className="text-sm text-stone-700 underline underline-offset-4"
              >
                {t("backToSignIn")}
              </button>
            </div>
          )}

          {mode === "form" && (
            <>
              <Tabs value={tab} onValueChange={setTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="signin" data-testid="tab-signin">{t("signInTab")}</TabsTrigger>
                  <TabsTrigger value="signup" data-testid="tab-signup">{t("signUpTab")}</TabsTrigger>
                </TabsList>

                <TabsContent value="signin">
                  <SignInForm
                    t={t}
                    onSuccess={(u) => { setUser(u); navigate("/dashboard"); }}
                    onForgot={() => setMode("forgot")}
                  />
                </TabsContent>

                <TabsContent value="signup">
                  <SignUpForm
                    t={t}
                    onSuccess={(u) => { setUser(u); navigate("/dashboard"); }}
                  />
                </TabsContent>
              </Tabs>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-stone-200" /></div>
                <div className="relative flex justify-center text-xs uppercase tracking-wider">
                  <span className="bg-white px-3 text-stone-400">{t("or")}</span>
                </div>
              </div>

              <button
                data-testid="google-signin-button"
                onClick={handleGoogleSignIn}
                className="w-full flex items-center justify-center gap-3 bg-white border border-stone-300 hover:border-stone-500 hover:shadow-sm rounded-xl px-6 py-3 text-sm font-medium text-stone-800 transition-all"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.836.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                  <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"/>
                </svg>
                {t("continueWithGoogle")}
              </button>

              <p className="text-[11px] text-stone-400 mt-5 text-center leading-relaxed">
                {t("signInFooter")}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SignInForm({ t, onSuccess, onForgot }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onSuccess(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="signin-form">
      <div className="space-y-1.5">
        <Label htmlFor="signin-email">{t("email")}</Label>
        <Input id="signin-email" data-testid="signin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" />
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor="signin-password">{t("password")}</Label>
          <button type="button" data-testid="forgot-password-link" onClick={onForgot} className="text-xs text-stone-500 hover:text-stone-900">{t("forgotPassword")}</button>
        </div>
        <Input id="signin-password" data-testid="signin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
      </div>
      {error && <p className="text-sm text-red-600" data-testid="signin-error">{error}</p>}
      <Button type="submit" data-testid="signin-submit" disabled={loading} className="w-full bg-stone-900 hover:bg-stone-800 text-white">
        {loading ? t("signingIn") : t("signIn")}
      </Button>
    </form>
  );
}

function SignUpForm({ t, onSuccess }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError(t("passwordTooShort"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      toast.success(t("accountCreated"));
      onSuccess(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
      <div className="space-y-1.5">
        <Label htmlFor="signup-name">{t("yourName")}</Label>
        <Input id="signup-name" data-testid="signup-name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("yourNamePlaceholder")} autoComplete="name" />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="signup-email">{t("email")}</Label>
        <Input id="signup-email" data-testid="signup-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="signup-password">{t("password")}</Label>
        <Input id="signup-password" data-testid="signup-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        <p className="text-xs text-stone-400">{t("passwordHint")}</p>
      </div>
      {error && <p className="text-sm text-red-600" data-testid="signup-error">{error}</p>}
      <Button type="submit" data-testid="signup-submit" disabled={loading} className="w-full bg-stone-900 hover:bg-stone-800 text-white">
        {loading ? t("creatingAccount") : t("createAccount")}
      </Button>
    </form>
  );
}

function ForgotForm({ t, onBack, onSent }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      onSent();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
      <div>
        <h2 className="font-heading text-2xl font-medium text-stone-900">{t("forgotTitle")}</h2>
        <p className="text-sm text-stone-500 mt-1">{t("forgotSubtitle")}</p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="forgot-email">{t("email")}</Label>
        <Input id="forgot-email" data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" />
      </div>
      {error && <p className="text-sm text-red-600" data-testid="forgot-error">{error}</p>}
      <div className="flex gap-2">
        <Button type="button" onClick={onBack} variant="outline" className="flex-1" data-testid="forgot-back">
          {t("back")}
        </Button>
        <Button type="submit" data-testid="forgot-submit" disabled={loading} className="flex-1 bg-stone-900 hover:bg-stone-800 text-white">
          {loading ? t("sending") : t("sendResetLink")}
        </Button>
      </div>
    </form>
  );
}
